import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { useFetch } from '../../components/component-items/helpers'
import { globalState } from '../../store'

// Components
import Pager, { Pages } from '../../components/component-items/pager'
import ButtonDropdown from '../../components/component-items/button-dropdown'
import CardLoading from '../../components/component-items/loading-popover'
import GlobalStyles from '../../components/component-items/styles'
import { OrderStatusBox, StatusBoxStyled } from '../../components/component-items/status-box'
import { TableCell, THSort } from '../../components/component-items/datatable'
import { Badge } from 'react-bootstrap'
import { TableRow } from './styles'
import { SearchBox } from '../../components/component-items/search'
import { ExportOrders } from './export'

// Fontawesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFire } from '@fortawesome/pro-duotone-svg-icons'
import { FilterOrders } from './filter'

// Transport Mode
import { TransportModeBadge } from '../../components/component-items/badges/transport-mode-badge'

export const OrderList = () => {
  let navigate = useNavigate()
  const location = useLocation()
  const urlParams = new URLSearchParams(location.search)
  const {
    state: { userIsWarehouse, actAs },
  } = globalState()

  const [page, setPage] = useState(1)
  const tableRowOptions = [20, 50, 100, 250, 500]
  const [rows, setRows] = useState(tableRowOptions[0])
  const [search, setSearch] = useState('')
  const [sku, setSku] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [statuses, setStatuses] = useState('')
  const [dateRangeType, setDateRangeType] = useState('')
  const [tags, setTags] = useState('')
  const [transportMode, setTransportMode] = useState<string[]>([])

  const sortOptions: any = {
    All: '',
    Unfulfilled: 'unfulfilled',
    'In Fulfillment': 'in_fulfillment',
    Fulfilled: 'fulfilled',
  }

  // Assign sort based on url params first
  const [sort, setSort] = useState('All')
  const [sortRow, setSortRow] = useState({})
  const [showFilterModal, setFilterModal] = useState(false)
  const [showExportModal, setExportModal] = useState(false)

  const apiUrlParams = new URLSearchParams({
    range: rows.toString(),
    page: (page - 1).toString(),
    fulfillment: sortOptions[sort],
    order_by: Object.keys(sortRow).join(','),
    sort_row: Object.values(sortRow).join(','),
    q: search,
    sku: sku,
    start_date: startDate,
    end_date: endDate,
    filter_status: statuses,
    date_range_type: dateRangeType,
    tags: tags,
    transport_mode: transportMode.join(','),
  })

  const url = `/api/order/order_list/?${apiUrlParams.toString()}`

  const { response: res, loaded, placeholder, error }: any = useFetch(url, {})

  const body = {
    range: rows,
    page: page - 1,
    fulfillment: sortOptions[sort],
    order_by: Object.keys(sortRow),
    sort_row: Object.values(sortRow),
    q: search,
    sku: sku,
    start_date: startDate,
    end_date: endDate,
    filter_status: statuses,
    date_range_type: dateRangeType,
    tags: tags,
    transport_mode: transportMode.join(','),
    download: true,
  }

  const [loading, setLoading] = useState(false)
  const [filterCount, setFilterCount] = useState(0)

  useEffect(() => {
    let counter = 0
    if (sort !== 'All') counter++
    if (statuses) counter++
    if (startDate || endDate) counter++
    if (transportMode.length > 0) counter++
    setFilterCount(counter)
  }, [startDate, endDate, statuses, sort, transportMode])

  useEffect(() => {
    setLoading(!loaded)
  }, [loaded])

  useEffect(() => {
    const status = urlParams.get('fulfillment')
    setSku(urlParams.get('sku') || '')
    setStartDate(urlParams.get('start_date') || '')
    setEndDate(urlParams.get('end_date') || '')
    setStatuses(urlParams.get('filter_status') || '')
    setDateRangeType(urlParams.get('date_range_type') || '')
    setTags(urlParams.get('tags') || '')
    const transportModeParam = urlParams.get('transport_mode') || ''
    setTransportMode(transportModeParam ? transportModeParam.split(',') : [])
    const index = status ? Object.values(sortOptions).indexOf(status) : 0
    setSort(Object.keys(sortOptions)[index])
    setPage(1)
  }, [location])

  const total = loaded ? res.total : 0
  var totalPages = loaded && total ? Math.ceil(total / rows) : 1

  const customSetSort = (e: any) => {
    const params = new URLSearchParams(location.search)
    params.set('fulfillment', sortOptions[e])
    navigate(`/order/?${params.toString()}`)
    setPage(1)
  }

  return (
    <GlobalStyles.FullPageCard>
      <GlobalStyles.CardHeader>
        <GlobalStyles.CardTitle>
          <h3>
            Order List<small>{total} Total</small>
          </h3>
        </GlobalStyles.CardTitle>
        <GlobalStyles.CardToolbar>
          <ButtonDropdown
            title={'Fulfillment'}
            sort={sort}
            setSort={customSetSort}
            options={Object.keys(sortOptions)}
          />
          <SearchBox
            style={{ marginLeft: '1em' }}
            {...{ search, setSearch, loading, setLoading, page, setPage }}
            setFocus
            updateURL
          />
          <GlobalStyles.Button onClick={() => setFilterModal(true)}>
            Filter {filterCount > 0 && <Badge pill>{filterCount}</Badge>}
          </GlobalStyles.Button>
          <FilterOrders {...{ showFilterModal, setFilterModal, setSort: customSetSort, setPage: setPage }} />
          {!userIsWarehouse || actAs ? (
            <>
              <GlobalStyles.Button
                className="secondary"
                onClick={() => {
                  setExportModal(true)
                }}
              >
                Export As CSV
              </GlobalStyles.Button>
              <ExportOrders {...{ showExportModal, setExportModal, body }} url={url + '&download=true'} />
            </>
          ) : null}
        </GlobalStyles.CardToolbar>
      </GlobalStyles.CardHeader>
      <GlobalStyles.DataTableContainer>
        <div style={{ minHeight: 500 }}>
          <GlobalStyles.DataTable>
            <thead>
              <tr>
                <THSort center title="Order Number" sortKey={'id'} sort={sortRow} setSort={setSortRow} />
                <THSort center title="Created" sort={sortRow} setSort={setSortRow} />
                <th>
                  <span className="text">Address</span>
                </th>
                <THSort title="Shipping" sort={sortRow} setSort={setSortRow} />
                <th>
                  <span className="text center">Tags</span>
                </th>
                <th>
                  <span className="text center">Order Items</span>
                </th>
                <THSort center title="Status" sort={sortRow} setSort={setSortRow} />
              </tr>
            </thead>
            <tbody>
              {loaded
                ? res.orders.map(
                    (
                      {
                        id,
                        remote_id,
                        name,
                        address,
                        shipping,
                        email,
                        created,
                        items,
                        status,
                        status_id,
                        b2b,
                        hot,
                        edi,
                        transport_mode,
                      }: any,
                      k_id: number
                    ) => (
                      <TableRow key={k_id}>
                        <TableCell
                          center
                          input={
                            <Link to={`/order/${id}/`} style={{ maxWidth: 150 }}>
                              {id}
                            </Link>
                          }
                        >
                          {remote_id ? <div>({remote_id})</div> : null}
                        </TableCell>
                        <TableCell center input={<div style={{ minWidth: 80 }}>{created.date}</div>}>
                          <div>{created.time}</div>
                        </TableCell>
                        <TableCell input={name}>
                          {address ? (
                            <>
                              <div>
                                {address.line1}
                                {address.line2 ? ' - ' + address.line2 : ''} {address.state} {address.zip}{' '}
                                {address.country_code}
                              </div>
                              <div>
                                {email} | {address.phone}
                              </div>
                            </>
                          ) : null}
                        </TableCell>
                        <TableCell input={shipping.method}>
                          {
                            <>
                              <div>{shipping.tracking_method}</div>
                              <div>
                                <a href={shipping.tracking_url} rel="noreferrer" target="__blank">
                                  {shipping.tracking}
                                </a>
                              </div>
                            </>
                          }
                        </TableCell>
                        <TableCell center>
                          {b2b && (
                            <StatusBoxStyled style={{ width: 75, margin: '0.5em auto' }} className={'purple bold'}>
                              B2B
                            </StatusBoxStyled>
                          )}
                          {hot && (
                            <StatusBoxStyled style={{ width: 75, margin: '0.5em auto' }} className={'red bold'}>
                              <FontAwesomeIcon icon={faFire} /> HOT
                            </StatusBoxStyled>
                          )}
                          {edi && (
                            <StatusBoxStyled style={{ width: 75, margin: '0.5em auto' }} className={'blue bold'}>
                              EDI
                            </StatusBoxStyled>
                          )}
                          <TransportModeBadge mode={transport_mode} />
                        </TableCell>
                        <TableCell>
                          {items.length ? (
                            <div
                              style={{
                                maxHeight: 200,
                                overflow: 'scroll',
                                boxShadow: 'rgba(67, 56, 93, 0.09) -4px 4px 15px 1px',
                              }}
                            >
                              <GlobalStyles.DataTable>
                                <tbody>
                                  {items.map(({ id, img, name, sku, qty }: any) => (
                                    <TableRow key={id}>
                                      <TableCell
                                        center
                                        input={
                                          <img src={img} style={{ height: 'auto', maxWidth: 50, maxHeight: 30 }} />
                                        }
                                      />
                                      <TableCell input={<Link to={`/product/variant/${id}/`}>{sku}</Link>}>
                                        <div>{name}</div>
                                      </TableCell>
                                      <TableCell input={qty} center />
                                    </TableRow>
                                  ))}
                                </tbody>
                              </GlobalStyles.DataTable>
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell
                          center
                          input={<OrderStatusBox style={{ width: 132, margin: 'auto' }} {...{ status, status_id }} />}
                        />
                      </TableRow>
                    )
                  )
                : null}
            </tbody>
          </GlobalStyles.DataTable>
        </div>
        <GlobalStyles.CardFooter>
          <Pages page={page} setPage={setPage} total={totalPages} />
          <Pager options={tableRowOptions} page={page} total={total} rows={rows} setRows={setRows} />
        </GlobalStyles.CardFooter>
        {!loaded ? <CardLoading text={placeholder} error={error} /> : null}
      </GlobalStyles.DataTableContainer>
    </GlobalStyles.FullPageCard>
  )
}
